import * as Sentry from "@sentry/react";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { Component } from "react";
import { useTranslation } from "react-i18next";

/**
 * Defense-in-depth for interactive islands (uploaders, forms): a component
 * crash must NEVER blank the whole screen (Block 4 P0). This is a safety
 * net — root causes still get fixed at the source.
 */
class Boundary extends Component {
  state = { crashed: false };

  static getDerivedStateFromError() {
    return { crashed: true };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", this.props.name || "component", error, info?.componentStack);
    // No-op si Sentry no está inicializado (sin DSN)
    Sentry.captureException(error, {
      tags: { boundary: this.props.name || "component" },
      contexts: { react: { componentStack: info?.componentStack } },
    });
  }

  render() {
    if (!this.state.crashed) return this.props.children;
    return (
      <div
        className="rounded-2xl border border-amber-200 bg-amber-50/60 p-6 flex flex-col items-start gap-3"
        role="alert"
        data-testid="component-error-fallback"
      >
        <div className="flex items-center gap-2.5">
          <AlertTriangle className="w-4 h-4 text-amber-600" aria-hidden="true" />
          <p className="text-sm font-medium text-[#0A0A0A]">{this.props.title}</p>
        </div>
        <button
          type="button"
          onClick={() => this.setState({ crashed: false })}
          data-testid="component-error-retry"
          className="inline-flex items-center gap-1.5 rounded-full h-9 px-4 bg-[#0A0A0A] text-white text-xs font-medium hover:bg-black/80 transition-colors duration-200"
        >
          <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" />
          {this.props.action}
        </button>
      </div>
    );
  }
}

export const ErrorBoundary = ({ name, children }) => {
  const { t } = useTranslation();
  return (
    <Boundary name={name} title={t("common.componentCrashed")} action={t("common.tryAgain")}>
      {children}
    </Boundary>
  );
};
